<div class="service-modification-page">
  <!-- Loading State -->
  @if (isLoading()) {
    <div class="loading-container">
      <div class="loading-spinner">
        <kendo-loader
          type="infinite-spinner"
          size="large"
        ></kendo-loader>
        <p>Loading customer data...</p>
      </div>
    </div>
  }

  <!-- Error State -->
  @if (error()) {
    <div class="error-container">
      <div
        class="alert alert-danger"
        role="alert"
      >
        <div class="d-flex align-items-center">
          <kendo-svg-icon
            [icon]="warningIcon"
            class="me-2"
          ></kendo-svg-icon>
          <span>{{ error() }}</span>
        </div>
        <button
          kendoButton
          themeColor="secondary"
          class="mt-3"
          (click)="onBack()"
        >
          Back to Customers
        </button>
      </div>
    </div>
  }

  <!-- Main Content -->
  @if (!isLoading() && !error()) {
    <form
      [formGroup]="modificationForm"
      (ngSubmit)="onSave()"
    >
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-content">
          <button
            kendoButton
            themeColor="secondary"
            size="medium"
            [svgIcon]="arrowLeftIcon"
            (click)="onBack()"
          >
            Back
          </button>
          <div class="header-text">
            <h2>Customer Details</h2>
          </div>
        </div>
        <div class="header-actions">
          <button
            kendoButton
            themeColor="error"
            size="medium"
            [svgIcon]="closeAccountIcon"
            (click)="onCloseAccount()"
          >
            Close Account
          </button>
        </div>
      </div>

      <!-- Customer Information -->
      <div class="customer-info-section">
        <div class="card">
          <div class="card-header">
            <h5>General Information</h5>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <label class="form-label">Customer Name</label>
                <input
                  type="text"
                  class="form-control"
                  formControlName="customerName"
                  readonly
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">Customer ID</label>
                <input
                  type="text"
                  class="form-control"
                  formControlName="customerId"
                  readonly
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">Customer Since</label>
                <input
                  type="text"
                  class="form-control"
                  formControlName="customerSince"
                  readonly
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">Customer Type</label>
                <input
                  type="text"
                  class="form-control"
                  formControlName="customerType"
                  readonly
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">Primary Contact</label>
                <input
                  type="text"
                  class="form-control"
                  formControlName="primaryContact"
                  readonly
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">Primary Contact Email</label>
                <input
                  type="text"
                  class="form-control"
                  formControlName="primaryContactEmail"
                  readonly
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Current Services -->
      <div class="current-services-section">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5>Current Services</h5>
            <div class="d-flex align-items-center gap-2">
              <span class="badge bg-primary">{{ getMockServices().length }} Services</span>
              <button
                kendoButton
                themeColor="primary"
                (click)="addServiceModification()"
              >
                Add Service
              </button>
            </div>
          </div>
          <div class="card-body">
            @if (getMockServices().length > 0) {
              <div class="services-grid">
                <div class="row g-4">
                  @for (service of getMockServices(); track service.id) {
                    <div class="col-lg-6">
                      <app-service-widget
                        [service]="service"
                        (onModify)="addServiceModification($event.serviceType, $event)"
                        (onCancel)="handleServiceCancellation($event)"
                      ></app-service-widget>
                    </div>
                  }
                </div>
              </div>
            } @else {
              <div class="text-center py-4">
                <p class="text-muted">No services found for this customer.</p>
                <button
                  kendoButton
                  themeColor="primary"
                  (click)="addServiceModification()"
                >
                  Add Service
                </button>
              </div>
            }
          </div>
        </div>
      </div>
    </form>
  }

  <!-- Close Account Confirmation Dialog -->
  @if (showCloseAccountDialog()) {
    <div
      class="modal-backdrop fade show"
      (click)="onCloseAccountDialogClose()"
    ></div>
    <div
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
    >
      <div
        class="modal-dialog modal-dialog-centered"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Close Account Confirmation</h5>
            <button
              type="button"
              class="btn-close"
              (click)="onCloseAccountDialogClose()"
              aria-label="Close"
            ></button>
          </div>

          <div class="modal-body">
            <div class="close-account-dialog-content">
              <div class="warning-section">
                <kendo-svg-icon
                  [icon]="warningIcon"
                  class="warning-icon"
                ></kendo-svg-icon>
                <h4>Are you sure you want to close this account?</h4>
              </div>

              <div class="consequences-section">
                <p><strong>This action will:</strong></p>
                <ul>
                  <li>Cancel all customer services immediately</li>
                  <li>Terminate all active subscriptions</li>
                  <li>Remove access to all customer data</li>
                  <li>Cannot be undone</li>
                </ul>
              </div>

              <div class="customer-info-section">
                <p>
                  <strong>Customer:</strong>
                  {{ modificationForm.get('customerName')?.value }}
                </p>
                <p>
                  <strong>Customer ID:</strong>
                  {{ modificationForm.get('customerId')?.value }}
                </p>
              </div>

              <div class="confirmation-section">
                <p class="text-danger">
                  <strong>Warning:</strong>
                  This action is permanent and cannot be reversed.
                </p>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              (click)="onCloseAccountDialogClose()"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              (click)="onConfirmCloseAccount()"
              [disabled]="isSaving()"
            >
              @if (isSaving()) {
                <span
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                Closing...
              } @else {
                Close Account
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  }
</div>
